
import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, XCircle, Users, AlertCircle } from 'lucide-react';

interface BatchInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const BatchInviteDialog: React.FC<BatchInviteDialogProps> = ({
  open,
  onOpenChange,
  onSuccess
}) => {
  const { toast } = useToast();
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [detailedError, setDetailedError] = useState<string | null>(null);

  const parseEmails = (emailText: string): string[] => {
    return emailText
      .split(/[,;\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setResults(null);
    setDetailedError(null);

    const emailList = parseEmails(emails);
    
    if (emailList.length === 0) {
      toast({
        variant: "destructive",
        title: "Feil",
        description: "Legg til minst én e-postadresse."
      });
      setSubmitting(false);
      return;
    }

    // Valider e-postadresser
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emailList.filter(email => !emailRegex.test(email));
    
    if (invalidEmails.length > 0) {
      toast({
        variant: "destructive",
        title: "Ugyldige e-postadresser",
        description: `Følgende e-postadresser er ugyldige: ${invalidEmails.join(', ')}`
      });
      setSubmitting(false);
      return;
    }

    try {
      console.log('Sending batch invitation with data:', { 
        emails: emailList, 
        role,
        emailCount: emailList.length 
      });

      const { data, error } = await supabase.functions.invoke('send-user-invitation', {
        body: { emails: emailList, role }
      });

      console.log('Supabase function response:', { data, error });

      if (error) {
        console.error('Supabase function error:', error);
        setDetailedError(`Edge Function feil: ${error.message}`);
        throw error;
      }

      if (!data) {
        const errorMsg = 'Ingen respons fra Edge Function';
        console.error(errorMsg);
        setDetailedError(errorMsg);
        throw new Error(errorMsg);
      }

      console.log('Invitation results:', data);
      setResults(data);
      
      if (data.success) {
        toast({
          title: "Invitasjoner sendt!",
          description: `${data.results?.length || 0} invitasjoner ble sendt successfully.`
        });
        
        if (data.errors && data.errors.length > 0) {
          toast({
            variant: "destructive",
            title: "Noen feil oppstod",
            description: `${data.errors.length} invitasjoner feilet.`
          });
        }
      } else {
        toast({
          variant: "destructive",
          title: "Invitasjoner feilet",
          description: data.message || "Ukjent feil oppstod"
        });
      }

    } catch (error: any) {
      console.error('Error sending batch invitations:', error);
      
      let errorMessage = 'Kunne ikke sende invitasjoner.';
      if (error.message) {
        errorMessage = error.message;
      }
      
      setDetailedError(errorMessage);
      
      toast({
        variant: "destructive",
        title: "Feil ved sending av invitasjoner",
        description: errorMessage
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (results?.success) {
      onSuccess();
    }
    setEmails('');
    setRole('user');
    setResults(null);
    setDetailedError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Inviter flere brukere
          </DialogTitle>
          <DialogDescription>
            Send invitasjoner til flere brukere samtidig ved å liste e-postadressene separert med komma, semikolon eller ny linje.
          </DialogDescription>
        </DialogHeader>

        {detailedError && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-800">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Detaljert feilmelding:</span>
              </div>
              <p className="text-red-700 mt-2 text-sm font-mono">{detailedError}</p>
            </CardContent>
          </Card>
        )}

        {!results ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emails">E-postadresser</Label>
              <Textarea
                id="emails"
                value={emails}
                onChange={(e) => setEmails(e.target.value.toLowerCase())}
                placeholder="bruker1@eksempel.no, bruker2@eksempel.no&#10;bruker3@eksempel.no"
                rows={8}
                required
              />
              <p className="text-sm text-muted-foreground">
                Legg til e-postadresser separert med komma, semikolon eller på separate linjer.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Rolle</Label>
              <Select value={role} onValueChange={(value: 'user' | 'admin') => setRole(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Bruker</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose}
              >
                Avbryt
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Sender..." : `Send ${parseEmails(emails).length || 0} invitasjoner`}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Resultater</h3>
              <p className="text-muted-foreground">
                {results.results?.length || 0} invitasjoner sendt, {results.errors?.length || 0} feilet
              </p>
            </div>

            {results.results && results.results.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Vellykkede invitasjoner ({results.results.length})
                  </h4>
                  <div className="space-y-1">
                    {results.results.map((result: any, index: number) => (
                      <div key={index} className="text-sm text-green-700">
                        • {result.email}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {results.errors && results.errors.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                    <XCircle className="h-4 w-4" />
                    Feilede invitasjoner ({results.errors.length})
                  </h4>
                  <div className="space-y-2">
                    {results.errors.map((error: any, index: number) => (
                      <div key={index} className="text-sm">
                        <div className="font-medium text-red-700">• {error.email}</div>
                        <div className="text-red-600 ml-4">{error.error}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end pt-4">
              <Button onClick={handleClose}>
                Lukk
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

